/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2024 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import {
  DateRange,
  IStudyDetail,
  OptionTypeBase,
  SearchPaginateType,
} from "@advapacs/common";
import { UserLightResponse } from "../types/user";
import { TaskStatus } from "../constant";

export interface ITaskConfiguration {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  completionType?: string;
}

export interface IUpdateTaskConfiguration {
  name: string;
  description?: string;
  enabled: boolean;
  completionType: string;
}

export interface ITaskConfigurationSearch extends SearchPaginateType {
  status?: boolean;
  searchKeyword?: string;
  completionType?: string;
}

export const defaultValueTaskConfiguration: Readonly<ITaskConfiguration> = {
  id: "",
  name: "",
  description: "",
  enabled: true,
  completionType: "ALL",
};

export interface ITaskTypeResponse {
  id?: string;
  name?: string;
  description?: string;
  enabled?: boolean;
}

export const defaultValueTask: Readonly<ITask> = {
  id: "",
  name: "",
  assignedTo: null,
  createdBy: null,
  createdAt: "",
  comment: "",
  completedComment: "",
  status: TaskStatus.TODO,
  completedBy: null,
  completedAt: "string",
  taskType: null,
};

export interface ITask {
  id: string;
  name: string;
  assignedTo: UserLightResponse[];
  createdBy: UserLightResponse;
  createdAt: string;
  comment: string;
  completedComment: string;
  status: TaskStatus;
  completedBy: UserLightResponse;
  completedAt: string;
  study?: IStudyDetail;
  taskType?: ITaskTypeResponse;
  updatedAt?: string;
}

export interface TaskSearchPrams extends SearchPaginateType {
  taskNames?: string;
  assignedByIds?: string[];
  assignedToIds?: string[];
  assignedAtStart?: string;
  assignedAtEnd?: string;
  statuses?: string[];
  patientName?: string;
  isFuzzySearch?: boolean;
  patientId?: string;
  isOwnTaskSearch?: boolean;
  taskTypeIds?: string[];
  patientIssuerIds?: string[];
}

export const initSearchParams: Readonly<TaskSearchPrams> = {
  taskNames: "",
  assignedByIds: null,
  assignedAtStart: null,
  assignedAtEnd: null,
  statuses: null,
  patientName: "",
  isFuzzySearch: false,
  patientId: "",
  size: 10,
  page: 0,
  taskTypeIds: null,
};

export interface TaskSearchState extends SearchPaginateType {
  taskNames?: OptionTypeBase[];
  createdBy?: UserLightResponse[];
  createdAt?: DateRange;
  statuses?: OptionTypeBase[];
  patientName?: string;
  isFuzzySearch?: boolean;
  patientId?: string;
  isOwnTaskSearch?: boolean;
  patientIssuerIds?: OptionTypeBase[];
  assignedTo?: UserLightResponse[];
}

export const initSearch: Readonly<TaskSearchState> = {
  taskNames: null,
  createdBy: null,
  createdAt: null,
  statuses: [{ label: "To Do", value: TaskStatus.TODO }],
  patientName: "",
  isFuzzySearch: false,
  patientId: "",
  isOwnTaskSearch: true,
  patientIssuerIds: null,
  assignedTo: null,
};

export interface ITaskForm {
  id: string;
  taskTypeId: string;
  taskType: OptionTypeBase;
  assignedTo: OptionTypeBase[];
  comment: string;
  status: OptionTypeBase;
  completedComment: string;
}

export const defaultValueTaskForm: Readonly<ITaskForm> = {
  id: "",
  taskTypeId: "",
  taskType: null,
  assignedTo: [],
  comment: "",
  status: null,
  completedComment: "",
};

export interface ITaskFormParams {
  taskTypeId: string;
  assignedToIds: string[];
  comment: string;
  status: string;
  completedComment: string;
}

export interface ICountTask {
  ownTaskCount: number;
  allTaskCount: number;
}
